import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useUpdateSuperAdminProfile,
  useChangeSuperAdminPassword,
} from '@/hooks/api/useSuperAdmins';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useThemeStore } from '@/store/themeStore';
import { formatDate } from '@/lib/date';
import { nameSchema, passwordSchema, confirmPasswordSchema } from '@/lib/validationSchemas';

const profileSchema = Yup.object().shape({
  name: nameSchema,
});

type ProfileFormData = Yup.InferType<typeof profileSchema>;

const passwordChangeSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: passwordSchema,
  confirmPassword: confirmPasswordSchema('newPassword'),
});

type PasswordFormData = Yup.InferType<typeof passwordChangeSchema>;

export default function MyAccount() {
  const { superAdmin } = useAuthStore();
  const { toast } = useToast();
  const { theme, setTheme } = useThemeStore();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [sessionStarted] = useState(() => new Date());
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);

  const updateProfileMutation = useUpdateSuperAdminProfile();
  const changePasswordMutation = useChangeSuperAdminPassword();

  const regenerateBackupCodesMutation = useMutation({
    mutationFn: () => authApi.regenerateBackupCodes(),
    onSuccess: (data) => {
      setNewBackupCodes(data.backupCodes);
      setBackupCodesDialogOpen(true);
      toast({
        title: 'Success',
        description: 'New backup codes generated. Store them safely.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to regenerate backup codes',
        variant: 'destructive',
      });
    },
  });

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: superAdmin?.name || '',
    },
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const handleUpdateProfile = (data: ProfileFormData) => {
    // Guard: only submit if we're actually in edit mode
    if (!isEditingProfile) return;

    updateProfileMutation.mutate(
      { name: data.name },
      {
        onSuccess: () => {
          setIsEditingProfile(false);
          resetProfile({ name: data.name });
        },
      },
    );
  };

  const handleChangePassword = (data: PasswordFormData) => {
    changePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          setIsChangingPassword(false);
          resetPassword();
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-foreground">My Account</h1>
        <p className="text-muted-foreground mt-1">Manage your super admin account settings</p>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...registerProfile('name')}
                disabled={!isEditingProfile}
                placeholder="Enter your name"
                hasError={!!profileErrors.name}
              />
              <ErrorMessage message={profileErrors.name?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={superAdmin?.email || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>Account created: {formatDate(superAdmin?.createdAt)}</p>
          </div>

          <Separator />

          <div className="flex gap-2">
            {isEditingProfile ? (
              <form onSubmit={handleProfileSubmit(handleUpdateProfile)} className="flex gap-2">
                <Button type="submit" disabled={updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={updateProfileMutation.isPending}
                  onClick={() => {
                    setIsEditingProfile(false);
                    resetProfile({ name: superAdmin?.name || '' });
                  }}
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <Button type="button" onClick={() => setIsEditingProfile(true)} variant="outline">
                Edit Profile
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          {!isChangingPassword ? (
            <Button onClick={() => setIsChangingPassword(true)} variant="outline">
              Change Password
            </Button>
          ) : (
            <form onSubmit={handlePasswordSubmit(handleChangePassword)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="Enter current password"
                  {...registerPassword('currentPassword')}
                  hasError={!!passwordErrors.currentPassword}
                />
                <ErrorMessage message={passwordErrors.currentPassword?.message} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password (min 8 characters)"
                  {...registerPassword('newPassword')}
                  hasError={!!passwordErrors.newPassword}
                />
                <ErrorMessage message={passwordErrors.newPassword?.message} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  {...registerPassword('confirmPassword')}
                  hasError={!!passwordErrors.confirmPassword}
                />
                <ErrorMessage message={passwordErrors.confirmPassword?.message} />
              </div>

              <Separator />

              <div className="flex gap-2">
                <Button type="submit" disabled={changePasswordMutation.isPending}>
                  {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={changePasswordMutation.isPending}
                  onClick={() => {
                    setIsChangingPassword(false);
                    resetPassword();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* 2FA Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>Manage your 2FA settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Status</p>
                <p className="text-sm text-muted-foreground">
                  {superAdmin?.twoFactorEnabled
                    ? '2FA is enabled for your account'
                    : '2FA is not yet configured for your account'}
                </p>
              </div>
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  superAdmin?.twoFactorEnabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}
              >
                {superAdmin?.twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication is mandatory for all super admin accounts and cannot be
                disabled.
              </p>
            </div>
            {superAdmin?.twoFactorEnabled && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Backup Codes</p>
                    <p className="text-sm text-muted-foreground">
                      Regenerate your 2FA backup codes. Old codes will be invalidated.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmRegenerateOpen(true)}
                    disabled={regenerateBackupCodesMutation.isPending}
                  >
                    Regenerate Backup Codes
                  </Button>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Session Information */}
      <Card>
        <CardHeader>
          <CardTitle>Session Information</CardTitle>
          <CardDescription>Details about your current session</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Account ID</p>
                <p className="text-sm text-muted-foreground font-mono mt-0.5">
                  {superAdmin?._id || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Account Status</p>
                <div className="mt-0.5">
                  <Badge
                    variant={superAdmin?.status === 'active' ? 'success' : 'default'}
                    className="capitalize"
                  >
                    {superAdmin?.status || 'Unknown'}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Session Started</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {sessionStarted.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Role</p>
                <p className="text-sm text-muted-foreground mt-0.5">Super Administrator</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Customize the look of the admin portal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label className="text-sm font-medium">Theme</Label>
            <div className="flex gap-3">
              {(
                [
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                  { value: 'system', label: 'System' },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                    theme === option.value
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border text-muted-foreground hover:border-border hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {theme === 'system'
                ? 'Automatically matches your operating system preference'
                : `Using ${theme} theme`}
            </p>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRegenerateOpen}
        onOpenChange={setConfirmRegenerateOpen}
        title="Regenerate Backup Codes"
        description="Are you sure you want to regenerate your backup codes? Your existing backup codes will be permanently invalidated."
        confirmLabel="Regenerate"
        variant="destructive"
        loading={regenerateBackupCodesMutation.isPending}
        onConfirm={() => regenerateBackupCodesMutation.mutate()}
      />

      <Dialog open={backupCodesDialogOpen} onOpenChange={setBackupCodesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Backup Codes</DialogTitle>
            <DialogDescription>
              Save these backup codes in a safe place. They will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 my-4">
            {newBackupCodes.map((code, index) => (
              <div
                key={index}
                className="font-mono text-sm bg-muted border border-border rounded px-3 py-2 text-center"
              >
                {code}
              </div>
            ))}
          </div>
          <Button onClick={() => setBackupCodesDialogOpen(false)} className="w-full">
            I have saved my backup codes
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
